import React from "react";

import clsx from "clsx";
import _ from "lodash";
import { Range } from "rc-slider";

import { NumberInput } from "@components/utilities";
import {
  type AlarmConditionV2,
  type AnomalyCondition,
  type CompositeAlarmConditionV2,
  isSimpleAlarmCondition,
  type MetricValueConfig,
  type SimpleAlarmConditionV2,
  type BreakdownConfig,
  type metrics as metricsPkg,
} from "@north-beam/nb-common";
import { enumType } from "@north-beam/nb-common";
import { IconButton } from "@shared/buttons";
import { Select } from "@shared/selects";

export function AlarmConditionsSelectorRoot(props: {
  setSelf: (value: AlarmConditionV2) => void;
  value: AlarmConditionV2;
  metrics: metricsPkg.MetricFragment[];
}) {
  const { setSelf, value, metrics } = props;

  return (
    <>
      <AlarmConditionsSelector {...props} />
      <HR />
      <div className="px-4 flex justify-between">
        <IconButton
          variant="tertiary"
          onClick={() =>
            setSelf({
              isLeaf: false,
              version: "v2",
              combinator: "and",
              children: [value, makeDefaultAlarmCondition(metrics)],
            })
          }
          leftIcon={<i className="fa-regular fa-sharp fa-plus-circle" />}
        >
          Add condition
        </IconButton>
        <IconButton
          disabled={isSimpleAlarmCondition(value)}
          onClick={() => {
            if (isSimpleAlarmCondition(value)) {
              return;
            }
            setSelf((value as CompositeAlarmConditionV2).children[0]);
          }}
          variant="danger"
          leftIcon={<i className="fa-regular fa-sharp fa-minus-circle" />}
        >
          Remove condition
        </IconButton>
      </div>
    </>
  );
}

function AlarmConditionsSelector(props: {
  setSelf: (value: AlarmConditionV2) => void;
  value: AlarmConditionV2;
  metrics: metricsPkg.MetricFragment[];
}) {
  const { setSelf, value, metrics } = props;

  return isSimpleAlarmCondition(value) ? (
    <SimpleAlarmConditionSelector
      setSelf={(value) => setSelf(value)}
      value={value}
    />
  ) : (
    <CompositeAlarmConditionSelector
      value={value}
      setSelf={(value) => setSelf(value)}
      metrics={metrics}
    />
  );
}

const HR = () => <hr style={{ marginLeft: -15, marginRight: -15 }} />;

function CompositeAlarmConditionSelector({
  value,
  setSelf,
  metrics,
}: {
  setSelf: (value: AlarmConditionV2) => void;
  value: CompositeAlarmConditionV2;
  metrics: metricsPkg.MetricFragment[];
}) {
  const { children, combinator } = value;

  const [first, second] = children;
  /**
   * 1
   *    - simple 1
   * 2
   *    - complex 3
   *      - simple 1
   *      - simple 2
   * 3
   *    - complex 5
   *      - complex 3
   *        - simple 1
   *        - simple 2
   *      - simple 4
   * 4
   *    - complex 7
   *      - complex 5
   *        - complex 3
   *          - simple 1
   *          - simple 2
   *        - simple 4
   *      - simple 6
   */

  const updateFirst = (val: AlarmConditionV2) => {
    setSelf({ ...value, children: [val, second] });
  };

  const updateSecond = (val: SimpleAlarmConditionV2) => {
    setSelf({ ...value, children: [first, val] });
  };

  const updateCombinator = (combinator: "and" | "or") => {
    setSelf({ ...value, combinator });
  };

  return (
    <>
      <AlarmConditionsSelector
        value={first}
        setSelf={updateFirst}
        metrics={metrics}
      />
      <HR />
      <div className="d-inline-block w-100">
        <div className="btn-group btn-group-toggle" data-toggle="buttons">
          <label
            className={clsx("button secondary-button", {
              active: combinator === "and",
            })}
            onClick={() => updateCombinator("and")}
          >
            <input
              type="radio"
              name="options"
              id="option1"
              checked={combinator === "and"}
            />{" "}
            AND
          </label>
          <label
            className={clsx("button secondary-button", {
              active: combinator === "or",
            })}
          >
            <input
              type="radio"
              name="options"
              id="option2"
              checked={combinator === "or"}
              onClick={() => updateCombinator("or")}
            />{" "}
            OR
          </label>
        </div>
      </div>
      <HR />
      <SimpleAlarmConditionSelector
        setSelf={updateSecond}
        value={second as SimpleAlarmConditionV2}
      />
    </>
  );
}

function SimpleAlarmConditionSelector(props: {
  setSelf: (value: SimpleAlarmConditionV2) => void;
  value: SimpleAlarmConditionV2;
}) {
  const { setSelf, value } = props;
  const { isDisabled, metrics } = useCtx();
  const { metric, currentValue, referenceValue, anomalyCondition } = value;

  const updateMetric = (m: metricsPkg.MetricFragment) =>
    setSelf({
      ...value,
      metric: m,
    });

  const updateCurrentValueWindow = (windowEndOffsetDays: number) =>
    setSelf({
      ...value,
      currentValue: { ...currentValue, windowEndOffsetDays },
    });

  const updateReferenceValue = (rv: MetricValueConfig) =>
    setSelf({
      ...value,
      referenceValue: rv,
    });

  const updateAnomalyCondition = (cond: AnomalyCondition) =>
    setSelf({
      ...value,
      anomalyCondition: cond,
    });

  return (
    <div>
      <Select
        label="Metric"
        tabIndex={10}
        isDisabled={isDisabled}
        placeholder="Select metric..."
        options={metrics.map((m) => ({
          value: m,
          label: m.name,
        }))}
        value={metric}
        onChange={updateMetric}
      />
      <HR />
      <NumberInput
        label="Window size days"
        tabIndex={30}
        disabled={isDisabled}
        value={currentValue.windowEndOffsetDays}
        format="integer"
        onValueChange={(v: number | undefined) => {
          if (typeof v === "number") {
            updateCurrentValueWindow(v);
          }
        }}
      />
      <HR />
      <MetricValueConfigEditor
        current={referenceValue}
        setValue={updateReferenceValue}
        tabIndex={40}
        metric={metric}
      />
      <HR />
      <AnomalyConditionEditor
        current={anomalyCondition}
        setValue={updateAnomalyCondition}
        tabIndex={50}
      />
    </div>
  );
}

function MetricValueConfigEditor({
  current,
  setValue,
  tabIndex,
  metric,
}: {
  current: MetricValueConfig;
  setValue: (value: MetricValueConfig) => void;
  tabIndex: number;
  metric: metricsPkg.MetricFragment;
}) {
  const { isDisabled } = useCtx();
  const updateType = (value: any) => {
    if (value === current.type) {
      return;
    }
    if (value === "AveragedValueConfig") {
      setValue({
        type: value,
        windowStartOffsetDays: 8,
        windowEndOffsetDays: 14,
        averagingMode: { type: "UniformAveragingMode" },
      });
    } else if (value === "FixedValueConfig") {
      setValue({
        type: value,
        value: 0,
      });
    }
  };

  const numericEditors =
    current.type === "FixedValueConfig" ? (
      <NumberInput
        tabIndex={tabIndex + 1}
        value={current.value}
        onValueChange={(value) => {
          if (typeof value === "number") {
            setValue({ ...current, value });
          }
        }}
        format={metric.format}
      />
    ) : (
      <div className="nb-month-interval-picker">
        <Range
          min={1}
          max={60}
          disabled={isDisabled}
          value={[current.windowStartOffsetDays, current.windowEndOffsetDays]}
          onChange={(value: number[]) => {
            const [lo, hi] = _.sortBy(value);
            setValue({
              ...current,
              windowStartOffsetDays: lo,
              windowEndOffsetDays: hi,
            });
          }}
        />
        <div className="text-center">
          {current.windowStartOffsetDays} days &ndash;{" "}
          {current.windowEndOffsetDays} days
        </div>
      </div>
    );
  return (
    <>
      <Select
        label="Reference value"
        className="mb-4"
        tabIndex={tabIndex}
        isDisabled={isDisabled}
        options={MetricValueType.reactSelectOptions}
        value={current.type}
        onChange={updateType}
      />
      {numericEditors}
    </>
  );
}

function AnomalyConditionEditor({
  current,
  setValue,
  tabIndex,
}: {
  current: AnomalyCondition;
  setValue: (value: AnomalyCondition) => void;
  tabIndex: number;
}) {
  const { isDisabled } = useCtx();
  const updateType = (value: any) => {
    if (value === current.type) {
      return;
    }
    if (value === "OutsidePercentageOfReferenceValue") {
      setValue({
        type: value,
        percentage: 0.2,
      });
    } else {
      setValue({ type: value });
    }
  };

  const numericEditors =
    current.type === "OutsidePercentageOfReferenceValue" ? (
      <>
        <HR />
        <NumberInput
          tabIndex={tabIndex + 1}
          value={current.percentage}
          onValueChange={(percentage) => {
            if (typeof percentage === "number") {
              setValue({ ...current, percentage });
            }
          }}
          format="percentage"
        />
      </>
    ) : null;

  return (
    <>
      <Select
        label="Anomaly condition"
        tabIndex={tabIndex}
        isDisabled={isDisabled}
        options={AnomalyConditionType.reactSelectOptions}
        value={current.type}
        onChange={updateType}
      />
      {numericEditors}
    </>
  );
}

const MetricValueType = enumType({
  enumObject: {
    FixedValueConfig: "Fixed value",
    AveragedValueConfig: "Moving average",
  },
  keyOrder: ["FixedValueConfig", "AveragedValueConfig"],
});

const AnomalyConditionType = enumType({
  enumObject: {
    AboveReferenceValue: "Above reference value",
    BelowReferenceValue: "Below reference value",
    OutsidePercentageOfReferenceValue: "Outside percentage of reference value",
  },
  keyOrder: [
    "AboveReferenceValue",
    "BelowReferenceValue",
    "OutsidePercentageOfReferenceValue",
  ],
});

export function makeDefaultAlarmCondition(
  metrics: metricsPkg.MetricFragment[],
): SimpleAlarmConditionV2 {
  return {
    version: "v2",
    isLeaf: true,
    metric: metrics[0],
    currentValue: {
      type: "AveragedValueConfig",
      windowStartOffsetDays: 1,
      windowEndOffsetDays: 7,
      averagingMode: { type: "UniformAveragingMode" },
    },
    referenceValue: {
      type: "FixedValueConfig",
      value: 0,
    },
    anomalyCondition: {
      type: "BelowReferenceValue",
    },
  };
}

export interface EditorContextType {
  isSaving: boolean;
  isDisabled: boolean;
  metrics: metricsPkg.MetricFragment[];
  breakdowns: BreakdownConfig[];
}

export const EditorContext = React.createContext<EditorContextType>({
  isSaving: false,
  isDisabled: true,
  metrics: [],
  breakdowns: [],
});
export const useCtx = () => React.useContext(EditorContext);
